import React, { useCallback, useEffect, useState } from 'react';
import CardShell from '../../components/CardShell';
import { Cell } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { getPlLanding, patchRealizedCumul, patchRemainingTotal } from '../../api/financials_pl';
import './PlanLandingSynthesis.css';

/* RÉSULTAT PRÉVISIONNEL › SYNTHÈSE (demande d'Olivier, 2026-09-27) : l'ATTERRISSAGE
   de l'exercice. Quatre tuiles, puis le compte de résultat simplifié et la table
   « Ratios et productivité », sur les mêmes colonnes : réalisé (mois clos), à
   venir (la version de plan choisie), atterrissage, exercice précédent, écart.
   Tout se calcule au serveur (`pl_landing_service`) à partir du même compte de
   résultat que les autres vues : l'atterrissage ne peut pas s'en écarter.
   Ventes & Ops se vend sans Finance : c'est ici qu'un tel client lit son
   atterrissage. */

const MOIS = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];
const COLS = ['real', 'todo', 'landing', 'n1'];

export default function PlanLandingSynthesis({ activeTenant, year, unit = 'kchf', canWrite = true, addToast }) {
  const p = useP();
  const tid = activeTenant?.id;
  const [v, setV] = useState(null);
  /* LE PERSONNEL SE DÉPLIE PAR COMPTE (demande d'Olivier, 2026-09-28). */
  const [openPers, setOpenPers] = useState(false);

  const load = useCallback(() => {
    if (!tid || !year) return Promise.resolve();
    return getPlLanding(tid, year)
      .then((r) => setV(r && !r.error && r.data && r.data.lines ? r.data : null))
      .catch(() => setV(null));
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  if (!tid) return null;
  if (!v) return <p className="pls-empty">{p('Chargement…')}</p>;

  const div = unit === 'chf' ? 1 : 1000;
  const uLbl = unit === 'chf' ? 'CHF' : 'kCHF';
  const num = (x, d = 0) => (x == null ? '–' : Number(x).toLocaleString('de-CH', { minimumFractionDigits: d, maximumFractionDigits: d }));
  const chf = (x) => (x == null ? '–' : (Math.abs(x / div) < 0.5 && div > 1 ? '–' : num(x / div)));
  const fmt = (row, x, delta = false) => {
    if (x == null) return '–';
    if (row.unit === 'pct') return `${num(x, 1)}${delta ? ' pts' : ' %'}`;
    if (row.unit === 'fte') return num(x, 1);
    return chf(x);
  };
  const cc = v.closed_count;
  /* Une saisie se tape dans l'unité affichée : en kCHF, 12 vaut 12'000. */
  const toChf = (x) => (x == null ? null : x * div);
  const save = (fn, payload) => fn(tid, { year, ...payload })
    .then((r) => { if (r && r.error && addToast) addToast(r.error, 'error'); return load(); });
  const persDetail = (v.details && v.details['sec:personnel']) || [];
  const range = (a, b) => (a > b ? '' : `${MOIS[a - 1]}–${MOIS[b - 1]}`);
  const heads = [
    { k: 'real', t: p('Réalisé'), s: range(1, cc) },
    { k: 'todo', t: p('À venir'), s: range(cc + 1, 12) },
    { k: 'landing', t: p('Atterrissage'), s: String(year), land: true },
    { k: 'n1', t: p('Exercice'), s: String(year - 1) },
  ];
  /* Un écart défavorable se signale (rare, charte) : un revenu, une marge ou
     un ratio qui baisse, une charge qui monte. L'effectif n'a pas de sens. */
  const alert = (row) => {
    const d = row.delta;
    if (d == null || Math.abs(d) < 1e-9 || row.unit === 'fte') return false;
    return row.kind === 'charge' ? d > 0 : d < 0;
  };

  /* LARGEUR : 800 px au minimum, puis selon le contenu (demande d'Olivier,
     2026-09-27). La carte ne s'étire pas sur l'écran ; elle s'élargit
     seulement quand les chiffres l'exigent (en CHF). */
  const table = (rows, first, isCascade) => (
    <table className="pls-tbl">
      <colgroup>
        <col className="pls-c-lbl" />
        {heads.map((h) => <col key={h.k} />)}
        <col />
      </colgroup>
      <thead>
        <tr>
          <th className="pls-lbl">{first}</th>
          {heads.map((h) => (
            <th key={h.k} className={h.land ? 'pls-land' : ''}>{h.t}<br /><span className="pls-sub">{h.s}</span></th>
          ))}
          <th>{p('Écart')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <React.Fragment key={r.code}>
          <tr className={isCascade && r.kind === 'sub' ? 'pls-subtot' : ''}>
            <td className="pls-lbl">
              {isCascade && r.code === 'sec:personnel' && persDetail.length > 0 && (
                <button type="button" className="pls-chev" aria-expanded={openPers}
                        onClick={() => setOpenPers((o) => !o)}>{openPers ? '▾' : '▸'}</button>
              )}
              {isCascade && r.kind === 'charge' ? '− ' : ''}{p(r.label)}
              {r.unit === 'pct' && <span className="pls-u"> {p('% du CAN')}</span>}
              {(r.code === 'ca_month' || r.code === 'ca_fte') && <span className="pls-u"> {uLbl}</span>}
              {r.code === 'fte' && <span className="pls-u"> · People</span>}
            </td>
            {COLS.map((k) => (
              <td key={k} className={k === 'landing' ? 'pls-land' : ''}>{fmt(r, r[k])}</td>
            ))}
            <td className={alert(r) ? 'pls-alert' : ''}>{fmt(r, r.delta, true)}</td>
          </tr>
          {isCascade && r.code === 'sec:personnel' && openPers && persDetail.map((d) => (
            <tr key={d.code} className="pls-detail">
              <td className="pls-lbl pls-lbl--sub">
                {d.label}{d.source === 'people' && <span className="pls-u"> · People</span>}
              </td>
              <td>
                <Cell value={d.real == null ? null : d.real / div} type="number" format={() => chf(d.real)}
                      canWrite={canWrite && d.edit_real}
                      onSave={(x) => save(patchRealizedCumul, { reporting_line_id: d.code, amount: toChf(x) })} />
              </td>
              <td>
                <Cell value={d.todo == null ? null : d.todo / div} type="number" format={() => chf(d.todo)}
                      canWrite={canWrite && d.edit_todo}
                      onSave={(x) => save(patchRemainingTotal, { reporting_line_id: d.code, total: toChf(x) || 0 })} />
              </td>
              <td className="pls-land">{chf(d.landing)}</td>
              <td>{chf(d.n1)}</td>
              <td>{chf(d.delta)}</td>
            </tr>
          ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );

  const by = Object.fromEntries(v.lines.map((x) => [x.code, x]));
  const rb = Object.fromEntries(v.ratios.map((x) => [x.code, x]));
  const ca = by['sec:revenue'];
  const ebitda = by.ebitda;
  const var_ = (a, b) => (a == null || !b ? null : Math.round(((a / b) - 1) * 100));
  const signe = (n) => (n == null ? '' : `${n > 0 ? '+' : ''}${n} %`);
  const tiles = [
    { l: `${p("Chiffre d'affaires")} ${year}`, v: chf(ca.landing),
      h: `${year - 1} : ${chf(ca.n1)} · ${signe(var_(ca.landing, ca.n1))}` },
    { l: `EBITDA ${year}`, v: chf(ebitda.landing),
      h: `${p('marge')} ${fmt({ unit: 'pct' }, rb['pct:ebitda']?.landing)} · ${year - 1} : ${chf(ebitda.n1)}` },
    { l: p('CA mensuel moyen'), v: `${chf(rb.ca_month.real)} → ${chf(rb.ca_month.todo)}`,
      h: `${p('réalisé → à venir')} · ${signe(var_(rb.ca_month.todo, rb.ca_month.real))}` },
    rb.ca_fte
      ? { l: p('CA annualisé par EPT'), v: `${chf(rb.ca_fte.real)} → ${chf(rb.ca_fte.todo)}`,
          h: `${p('réalisé → à venir')} · ${year - 1} : ${chf(rb.ca_fte.n1)}` }
      : { l: `${p('Résultat net')} ${year}`, v: chf(by.net_result.landing), h: `${year - 1} : ${chf(by.net_result.n1)}` },
  ];

  return (
    <div className="pls">
      <div className="pls-tiles">
        {tiles.map((t) => (
          <div key={t.l} className="pls-tile">
            <span className="pls-tile__l">{t.l}</span>
            <span className="pls-tile__v">{t.v}</span>
            <span className="pls-tile__h">{t.h}</span>
          </div>
        ))}
      </div>
      <CardShell className="pls-card" title={`${p('Compte de résultat')} · ${p('atterrissage')} ${year} · ${uLbl}`} flush>
        {table(v.lines, p('Compte de résultat'), true)}
      </CardShell>
      <CardShell className="pls-card" title={p('Ratios et productivité')} flush>
        {table(v.ratios, p('Ratio'), false)}
      </CardShell>
    </div>
  );
}
